import React, { useContext, useEffect } from 'react';
import { equals, has, reduce, reduced } from 'ramda';
import { JSONObject, JSONValue } from '../lib/types';

/**
Companion type guard for JSONObject
*/
export const isJSONTreatment = (treatment: FeatureFlagVariationValue): treatment is JSONObject =>
  treatment != null && typeof treatment === 'object';

/**
 * FeatureFlagVariationValue:
 * The value of one evaluated feature flag.
 */
export type FeatureFlagVariationValue = NonNullable<JSONValue>;

export interface FeatureFlagProp {
  flagName: string;
  flagValue: FeatureFlagVariationValue | undefined;
}

/**
 * The reason code for how a flag evaulation got its value.
 *
 * - `'OFF'`: The flag was off and therefore returned its configured off value.
 * - `'FALLTHROUGH'`: The flag was on but the user did not match any targets or rules.
 * - `'TARGET_MATCH'`: The user key was specifically targeted for this flag.
 * - `'RULE_MATCH'`: the user matched one of the flag's rules.
 * - `'PREREQUISITE_FAILED'`: The flag was considered off because it had at least one
 *   prerequisite flag that either was off or did not return the desired variation.
 * - `'ERROR'`: The flag could not be evaluated, e.g. because it does not exist or due
 *   to an unexpected error.
 */
export type FeatureFlagEvaluationReason =
  | 'OFF'
  | 'FALLTHROUGH'
  | 'TARGET_MATCH'
  | 'RULE_MATCH'
  | 'PREREQUISITE_FAILED'
  | 'ERROR';

/**
 * One feature flag result, which includes the value and the reason.
 */
export type FeatureFlagResult = {
  value: FeatureFlagVariationValue;
  reason: FeatureFlagEvaluationReason;
};

/**
 * The map of flag names to results.
 */
export interface FeatureFlagResults {
  [flagName: string]: FeatureFlagResult;
}

type FeatureFlagContextState = {
  flags: FeatureFlagResults;
  onFlagRequested?: (
    flagName: string,
    treatment: FeatureFlagVariationValue,
    reason?: FeatureFlagEvaluationReason
  ) => void;
};

const FeatureFlagContext = React.createContext<FeatureFlagContextState>({ flags: {} });

export const FeatureFlagProvider: React.FC<
  FeatureFlagContextState & { children?: React.ReactNode }
> = ({ children, ...rest }) => (
  <FeatureFlagContext.Provider value={rest}>{children}</FeatureFlagContext.Provider>
);

const isUnavailable = function (item: FeatureFlagResults) {
  return (
    item == null ||
    (typeof item === 'object' && !Array.isArray(item) && Object.keys(item).length === 0)
  );
};

interface FeatureFlagState {
  isAvailable: boolean;
  treatment?: FeatureFlagVariationValue;
  reason?: FeatureFlagEvaluationReason;
}

export const useFeatureFlagState = (flagName: string): FeatureFlagState => {
  const { flags, onFlagRequested } = useContext(FeatureFlagContext);

  const applicableFlag = flags[flagName];

  const treatment: NonNullable<JSONValue> | undefined = applicableFlag?.value;
  const reason: FeatureFlagEvaluationReason | undefined = applicableFlag?.reason;

  useEffect(() => {
    if (treatment != null) onFlagRequested?.(flagName, treatment, reason);
  }, [flagName, onFlagRequested, treatment, reason]);

  if (isUnavailable(flags) || !has(flagName, flags)) {
    return { isAvailable: false };
  }

  return { isAvailable: true, treatment, reason };
};

export type FeatureFlagTreatmentCondition<T> = {
  if: FeatureFlagVariationValue;
  then: T;
};

export type FeatureFlagTreatmentFunctionCondition<T> = {
  getFromTreatment: (treatment: FeatureFlagVariationValue) => T;
};

export type FeatureFlagUnavailableCondition<T> = { ifFlagIsUnavailable: T };

export type FeatureFlagDefaultCondition<T> = { default: T };

export type FeatureFlagCondition<T> =
  | FeatureFlagTreatmentCondition<T>
  | FeatureFlagTreatmentFunctionCondition<T>
  | FeatureFlagUnavailableCondition<T>
  | FeatureFlagDefaultCondition<T>;

function isTreatmentCondition<T>(
  condition: FeatureFlagCondition<T>
): condition is FeatureFlagTreatmentCondition<T> {
  return Object.keys(condition).includes('if');
}

function isTreatmentFunctionCondition<T>(
  condition: FeatureFlagCondition<T>
): condition is FeatureFlagTreatmentFunctionCondition<T> {
  return Object.keys(condition).includes('getFromTreatment');
}

function isUnavailableCondition<T>(
  condition: FeatureFlagCondition<T>
): condition is FeatureFlagUnavailableCondition<T> {
  return Object.keys(condition).includes('ifFlagIsUnavailable');
}

/**
 * A hook for getting a value based on a feature flag.
 * Example usages:
 * `useFeatureFlag('password-min-length', [{ default: 6 }, { if: true, then: 10 }])`
 * `useFeatureFlag('products-per-page', [{ default: 60 }, { getFromTreatment: treatment => Number(treatment) ?? 60 }])`
 */
export function useFeatureFlag<T>(
  featureName: string,
  conditions: [FeatureFlagDefaultCondition<T>, ...FeatureFlagCondition<T>[]]
) {
  const { isAvailable, treatment } = useFeatureFlagState(featureName);

  return reduce(
    (memo, condition) => {
      if (isTreatmentCondition(condition) && isAvailable && equals(condition.if, treatment)) {
        return reduced(condition.then);
      }
      if (isTreatmentFunctionCondition(condition) && isAvailable && treatment != null) {
        return reduced(condition.getFromTreatment(treatment));
      }
      if (isUnavailableCondition(condition) && !isAvailable) {
        return reduced(condition.ifFlagIsUnavailable);
      }
      return memo;
    },
    conditions[0].default,
    conditions
  );
}

export interface BehindFeatureFlagProps {
  flagName: string;
  showWhenValueIs: FeatureFlagVariationValue;
  showIfFlagIsUnavailable?: boolean;
  children?: React.ReactNode;
}

/**
 * Show something only when a feature flag condition is met.
 * Example usage: `<BehindFeatureFlag flagName="my-flag" showWhenValueIs={true}>...</BehindFeatureFlag>`
 */
export const BehindFeatureFlag: React.FC<BehindFeatureFlagProps> = function (props) {
  const { children, flagName, showWhenValueIs, showIfFlagIsUnavailable } = props;
  const { isAvailable, treatment } = useFeatureFlagState(flagName);

  if (!isAvailable) {
    return <>{showIfFlagIsUnavailable ? children : null}</>;
  }

  const shouldShow = equals(treatment, showWhenValueIs);

  return <>{shouldShow ? children : null}</>;
};
